
import * as React from "react";
import type { Initiative, Person, Status, Update } from "@/lib/types";
import { TEAMS, ALL_TAGS, tagPickerOptions, pruneTagsForTeam, tagNum, STATUS_LABELS, STATUS_KEYS, DESCRIPTION_MAX, DESCRIPTION_COUNTER_AT, DESCRIPTION_PLACEHOLDER, toDate, fmtDateLong, quarterForDate, quarterLabel, localISO, isoToTuple } from "@/lib/constants";
import { AUTH_ENABLED } from "@/lib/config";
import { useInitiativeUpdates } from "@/lib/store";
import { renderUpdateBody, hasRealContent, BARE_BULLET_RE, BULLET_PREFIX_RE } from "@/lib/update-markdown";
import { shipDeltaDays, isCommittedStatus, actualShipped } from "@/lib/baseline";
import { jiraDisplay, jiraHref } from "@/lib/utils";
import { Avatar, StatusPill, DescriptionCounter } from "./primitives";
import { Icon } from "./icons";
import { DriPicker, UnlinkedBadge } from "./dri-picker";
import { dependencyCandidates, directDependents } from "@/lib/dependencies";
import { workstreamNames } from "@/lib/group-by";

// Drawer's in-progress edit buffer. Mirrors a subset of Initiative fields,
// plus a string-typed targetISO for the date input element.
// Keep field names aligned with Initiative so the save handler can spread cleanly.
type EditState = Pick<Initiative, "name" | "tldr" | "status" | "teamId" | "podId" | "driId" | "pillarIds"> & {
  targetISO: string;  // the estimate — quarter is derived from it on save
  startISO: string;   // actually began (auto-stamped on In Progress; correctable here)
  shippedISO: string; // actually done (auto-stamped on Shipped; correctable here)
  jira: string;
  notion: string;
  design: string;
  dependsOnId: string; // "" = none
  workstream: string;  // "" = none
};

// The gentle weekly-format nudge: prefilled by the "Use weekly format" button,
// fully editable, never required. Unfilled "- " bullets are dropped at render.
const WEEKLY_TEMPLATE = "**Progress**\n- \n\n**Challenges**\n- \n\n**Next week**\n- ";
// Caret lands on the first bullet — derived, so editing the template text
// can't silently strand the cursor mid-string.
const TEMPLATE_CARET = WEEKLY_TEMPLATE.indexOf("- ") + 2;
// A weekly update is a paragraph, not a document — cap what one row can make
// every viewer's drawer render (unbounded bodies are a stored render-bomb).
const UPDATE_BODY_MAX = 10_000;
const COMPOSER_MAX_HEIGHT = 280;

function useFocusTrap(open: boolean, ref: React.RefObject<HTMLElement | null>) {
  React.useEffect(() => {
    if (!open || !ref.current) return;
    const prev = document.activeElement as HTMLElement | null;
    const container = ref.current;
    const SEL = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusables = () => Array.from(container.querySelectorAll<HTMLElement>(SEL)).filter(el => !el.hasAttribute("aria-hidden"));
    // Initial focus lands on the first control that hasn't opted out — quiet
    // affordances (the "Add a description" link) stay in the Tab order but
    // shouldn't be what Enter hits the instant the modal opens.
    const initial = () => focusables().find(el => !el.hasAttribute("data-skip-initial-focus")) ?? focusables()[0] ?? container;
    const t = setTimeout(() => { initial().focus(); }, 50);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const f = focusables();
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      if (prev?.focus) prev.focus();
    };
  }, [open, ref]);
}

export function Drawer({
  initiative,
  roster,
  currentUser,
  onClose,
  onUpdate,
  onArchive,
  onPostUpdate,
  onPickPerson,
  initiatives = [],
  onOpen,
}: {
  initiative: Initiative | null;
  /** Every active initiative — the "Depends on" picker and "Followed by" list. */
  initiatives?: Initiative[];
  /** Jump to another initiative (a "Depends on" / "Followed by" link). */
  onOpen?: (i: Initiative) => void;
  roster: Person[];
  /** Signed-in user (auth on) — authors updates as themselves. Null when auth
   *  is off, which falls back to picking an author from the roster. */
  currentUser?: Person | null;
  onClose: () => void;
  onUpdate: (id: string, patch: Partial<Initiative>) => void;
  onArchive: (id: string) => void;
  /** Directory pick → roster row, for changing the owner in edit mode. */
  onPickPerson: (name: string, email: string) => Person;
  onPostUpdate: (id: string, input: { body: string; status: Status; authorId?: string | null; start?: [number, number, number]; shipped?: [number, number, number] }) => Update;
}) {
  const open = !!initiative;
  const [editing, setEditing] = React.useState(false);
  const [edit, setEdit] = React.useState<EditState | null>(null);
  const drawerRef = React.useRef<HTMLDivElement>(null);
  useFocusTrap(open, drawerRef);
  // "Add a description" (historical rows with none) opens edit mode with the
  // description field focused — the flag is consumed once edit mode mounts.
  const tldrRef = React.useRef<HTMLTextAreaElement>(null);
  const focusTldrOnEdit = React.useRef(false);

  // Weekly updates for the open initiative (timeline + post form).
  const { updates, hydrated: updatesHydrated, prepend } = useInitiativeUpdates(initiative?.id);

  // "Depends on": the predecessor, direct followers, and the picker's options
  // (grouped by team; never itself or its own dependents — that's a loop).
  const predecessor = initiative?.dependsOnId ? initiatives.find(i => i.id === initiative.dependsOnId) : undefined;
  const followers = React.useMemo(
    () => (initiative ? directDependents(initiative.id, initiatives) : []),
    [initiative, initiatives],
  );
  const workstreams = React.useMemo(() => workstreamNames(initiatives), [initiatives]);
  const dependencyGroupsByTeam = React.useMemo(() => {
    if (!initiative) return [];
    const options = dependencyCandidates(initiative.id, initiatives).sort((a, b) => a.name.localeCompare(b.name));
    const groups = TEAMS.map(t => ({ id: t.id, name: t.name, items: options.filter(i => i.teamId === t.id) }));
    const other = options.filter(i => !TEAMS.some(t => t.id === i.teamId));
    if (other.length) groups.push({ id: "_other", name: "Other", items: other });
    return groups.filter(g => g.items.length > 0);
  }, [initiative, initiatives]);
  const [draftBody, setDraftBody] = React.useState("");
  const [draftStatus, setDraftStatus] = React.useState<Status>("in-progress");
  // Transition dates for the composer. Posting an update can move the status —
  // the first move into in-progress/shipped needs the real-world date confirmed
  // (backdatable), not a silent "today" stamp.
  const [transStartISO, setTransStartISO] = React.useState("");
  const [transShippedISO, setTransShippedISO] = React.useState("");
  const startsWork = !!initiative && isCommittedStatus(draftStatus) && !isCommittedStatus(initiative.status);
  const shipsNow = !!initiative && draftStatus === "shipped" && !actualShipped(initiative);
  const prevStartsWork = React.useRef(false);
  const prevShipsNow = React.useRef(false);

  // Initialize a transition-date field only when it APPEARS (hidden -> shown);
  // never clobber a value the user already typed — flipping the status
  // dropdown must not silently discard a carefully entered backdate.
  React.useEffect(() => {
    if (!initiative) return;
    if (startsWork && !prevStartsWork.current) {
      // Default the start to the planned start when one exists; today otherwise.
      const planned = initiative.start ? toDate(initiative.start) : undefined;
      setTransStartISO(localISO(planned ?? new Date()));
    } else if (!startsWork && prevStartsWork.current) {
      setTransStartISO("");
    }
    if (shipsNow && !prevShipsNow.current) setTransShippedISO(localISO(new Date()));
    else if (!shipsNow && prevShipsNow.current) setTransShippedISO("");
    prevStartsWork.current = startsWork;
    prevShipsNow.current = shipsNow;
  }, [startsWork, shipsNow, initiative]);

  // Facts can't be out of order: started must not come after shipped, and a
  // ship date can't precede the initiative's committed start.
  const existingStartISO = initiative?.start ? localISO(toDate(initiative.start)!) : "";
  const transInvalid = !!(
    (startsWork && shipsNow && transStartISO && transShippedISO && transStartISO > transShippedISO) ||
    (shipsNow && !startsWork && existingStartISO && transShippedISO && transShippedISO < existingStartISO)
  );

  React.useEffect(() => {
    setEditing(false);
    setEdit(null);
    setDraftBody("");
    setDraftStatus(initiative?.status ?? "in-progress");
    // A different initiative means the transition-date fields start over.
    prevStartsWork.current = false;
    prevShipsNow.current = false;
    // Reset the post form only when a different initiative opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiative?.id]);

  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape" && open) onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [open, onClose]);

  const team = initiative ? TEAMS.find(t => t.id === initiative.teamId) : undefined;
  const pod = team && initiative ? team.pods.find(p => p.id === initiative.podId) : undefined;
  const dri = initiative ? roster.find(p => p.id === initiative.driId) : undefined;
  const targetDate = initiative ? toDate(initiative.target) : undefined;
  const originalTargetDate = initiative?.originalTarget ? toDate(initiative.originalTarget) : undefined;
  // Baseline only earns a row once it differs from the current estimate.
  const slipped = !!(originalTargetDate && targetDate && originalTargetDate.getTime() !== targetDate.getTime());
  const startDate = initiative?.start ? toDate(initiative.start) : undefined;
  const shippedDate = initiative ? toDate(actualShipped(initiative)) : undefined;
  // The retro sentence: shipped vs the at-start estimate.
  const delta = initiative ? shipDeltaDays(initiative) : null;
  const pillars = initiative?.pillarIds
    .map(pid => ALL_TAGS.find(p => p.id === pid))
    .filter((p): p is NonNullable<typeof p> => Boolean(p)) ?? [];

  const startEdit = (focus?: "tldr") => {
    if (!initiative) return;
    const t = toDate(initiative.target);
    const s = initiative.start ? toDate(initiative.start) : undefined;
    const sh = toDate(actualShipped(initiative));
    focusTldrOnEdit.current = focus === "tldr";
    setEdit({
      name: initiative.name,
      tldr: initiative.tldr,
      status: initiative.status,
      teamId: initiative.teamId,
      podId: initiative.podId || "",
      driId: initiative.driId,
      pillarIds: [...initiative.pillarIds],
      targetISO: t ? localISO(t) : "",
      startISO: s ? localISO(s) : "",
      shippedISO: sh ? localISO(sh) : "",
      jira: initiative.jira ?? "",
      notion: initiative.notion ?? "",
      design: initiative.design ?? "",
      dependsOnId: initiative.dependsOnId ?? "",
      workstream: initiative.workstream ?? "",
    });
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setEdit(null); };
  React.useEffect(() => {
    if (editing && focusTldrOnEdit.current) {
      focusTldrOnEdit.current = false;
      tldrRef.current?.focus();
    }
  }, [editing]);
  // Started must not come after Shipped (when both are set) — gates Save.
  const datesInvalid = !!(edit && edit.startISO && edit.shippedISO && edit.startISO > edit.shippedISO);
  // The target must land inside a defined fiscal year — otherwise target and
  // targetQ diverge (a 2028 date filed under an FY26 quarter). Mirrors the
  // create modal's gate.
  const targetOutside = !!(edit && edit.targetISO && !quarterForDate(new Date(edit.targetISO + "T00:00:00")));
  // Pending status transitions — each one owns a date the user must confirm.
  // The prompt renders in the header, right under the status control.
  const pendingStart = !!(editing && edit && initiative && edit.status !== initiative.status && isCommittedStatus(edit.status) && !isCommittedStatus(initiative.status));
  const pendingShip = !!(editing && edit && initiative && edit.status === "shipped" && initiative.status !== "shipped");
  const reopening = !!(editing && edit && initiative && initiative.status === "shipped" && edit.status !== "shipped");
  // A pending transition without its date is not saveable — the whole point
  // is that these dates are never silently defaulted.
  const transitionMissing = !!((pendingStart && !edit?.startISO) || (pendingShip && !edit?.shippedISO));
  // "Required going forward" has to survive edit mode: a row that already has
  // a description can't be saved without one. Rows that were born empty
  // (pre-2026-09 backlog) stay lenient so a date fix never demands prose.
  const descriptionCleared = !!(edit && initiative && initiative.tldr.trim() && !edit.tldr.trim());
  // The owner picker can be cleared; an initiative can't be saved ownerless
  // (the create modal never allows it either, and Slack routing needs a DRI).
  const ownerMissing = !!(edit && !edit.driId);
  const saveBlocked = datesInvalid || targetOutside || transitionMissing || descriptionCleared || ownerMissing;
  const saveEdit = () => {
    if (!edit || !initiative || saveBlocked) return;
    const patch: Partial<Initiative> = {
      name: edit.name.trim() || initiative.name,
      tldr: edit.tldr.trim(),
      status: edit.status,
      teamId: edit.teamId,
      podId: edit.podId,
      driId: edit.driId,
      pillarIds: edit.pillarIds,
      jira: edit.jira.trim() || undefined,
      notion: edit.notion.trim() || undefined,
      design: edit.design.trim() || undefined,
      dependsOnId: edit.dependsOnId || undefined,
      workstream: edit.workstream.trim() || undefined,
    };
    // Target date drives the quarter. Legacy rows without a date keep their
    // quarter until someone sets one — clearing the field leaves both as-is.
    if (edit.targetISO) {
      const d = new Date(edit.targetISO + "T00:00:00");
      patch.target = [d.getFullYear(), d.getMonth(), d.getDate()];
      patch.targetQ = quarterForDate(d) ?? initiative.targetQ;
    }
    // Started/Shipped corrections: set, or clear when the field was emptied.
    patch.start = edit.startISO ? isoToTuple(edit.startISO) : undefined;
    // Only a Shipped item carries a ship date; saving any other status clears it.
    patch.shipped = edit.status === "shipped" && edit.shippedISO ? isoToTuple(edit.shippedISO) : undefined;
    onUpdate(initiative.id, patch);
    setEditing(false);
    setEdit(null);
  };
  const editField = <K extends keyof EditState>(k: K, v: EditState[K]) => setEdit(prev => {
    if (!prev || !initiative) return prev;
    const next = { ...prev, [k]: v };
    if (k === "teamId" && v !== prev.teamId) {
      next.podId = "";
      next.pillarIds = pruneTagsForTeam(v as string, "", prev.pillarIds); // tags are scoped by team/pod
    }
    if (k === "podId" && v !== prev.podId) {
      next.pillarIds = pruneTagsForTeam(next.teamId, v as string, prev.pillarIds);
    }
    // Same rule as the New initiative form: moving the start onto or past
    // the target pushes the target to the day after. An empty target stays.
    if (k === "startISO" && v && next.targetISO && next.targetISO <= (v as string)) {
      const d = new Date((v as string) + "T00:00:00");
      d.setDate(d.getDate() + 1);
      next.targetISO = localISO(d);
    }
    // Status changes drive the transition prompt (rendered in the header):
    // prefill the real-world dates so the user confirms or backdates instead
    // of getting a silent "today" stamp on save.
    if (k === "status") {
      const s = v as Status;
      if (s === initiative.status) {
        // Toggled away and back — restore the saved dates so nothing is
        // silently cleared or re-stamped by an aborted status change.
        const st = initiative.start ? toDate(initiative.start) : undefined;
        const sh = toDate(actualShipped(initiative));
        next.startISO = st ? localISO(st) : "";
        next.shippedISO = sh ? localISO(sh) : "";
      } else {
        if (isCommittedStatus(s) && !isCommittedStatus(initiative.status) && !next.startISO) next.startISO = localISO(new Date());
        if (s === "shipped" && !next.shippedISO) next.shippedISO = localISO(new Date());
        // Reopening a shipped item: the ship date is no longer a fact.
        if (s !== "shipped" && initiative.status === "shipped") next.shippedISO = "";
      }
    }
    return next;
  });

  const togglePillar = (pid: string) => {
    if (!edit) return;
    const cur = edit.pillarIds;
    editField("pillarIds", cur.includes(pid) ? cur.filter(x => x !== pid) : [...cur, pid]);
  };

  const submitUpdate = () => {
    if (!initiative || !hasRealContent(draftBody) || transInvalid) return;
    // Authorship is never a choice: the signed-in identity, or nobody.
    // With auth on, an unresolved identity blocks posting entirely.
    if (AUTH_ENABLED && !currentUser) return;
    const created = onPostUpdate(initiative.id, {
      body: draftBody.trim().slice(0, UPDATE_BODY_MAX),
      status: draftStatus,
      authorId: currentUser?.id ?? null,
      // User-confirmed transition dates ride along; the store stamps today for
      // any the user cleared.
      start: startsWork && transStartISO ? isoToTuple(transStartISO) : undefined,
      shipped: shipsNow && transShippedISO ? isoToTuple(transShippedISO) : undefined,
    });
    prepend(created);
    setDraftBody("");
  };

  // --- Composer formatting aids ------------------------------------------
  // Edits go through execCommand("insertText") where possible — deprecated,
  // but it's the only way to keep the textarea's native undo stack working
  // (a plain setState edit makes Cmd+Z useless). setState is the fallback.
  const bodyRef = React.useRef<HTMLTextAreaElement>(null);

  // Auto-grow the composer to its content (capped) — the weekly template is
  // 8 lines and must not pour into a 2-row letterbox.
  // `editing` in the deps: the Updates section unmounts while editing, so the
  // effect must re-run when the textarea remounts or a multi-line draft comes
  // back letterboxed at rows=2.
  React.useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight + 2, COMPOSER_MAX_HEIGHT)}px`;
  }, [draftBody, editing]);

  const applyEdit = (start: number, end: number, text: string, caret?: [number, number]) => {
    const el = bodyRef.current;
    if (!el) return;
    const expected = el.value.slice(0, start) + text + el.value.slice(end);
    el.focus();
    el.setSelectionRange(start, end);
    let ok = false;
    // insertText no-ops on an empty string — deletions go through "delete".
    try {
      ok = text
        ? document.execCommand("insertText", false, text)
        : document.execCommand("delete");
    } catch { ok = false; }
    if (!ok || el.value !== expected) {
      // execCommand refused (some engines, synthetic events). Write the value
      // natively and fire input so React syncs state FROM the DOM — a plain
      // setState here loses a race with the next keystroke's onChange.
      const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, "value")?.set;
      if (setter) setter.call(el, expected);
      else el.value = expected;
      el.dispatchEvent(new Event("input", { bubbles: true }));
    }
    const at = caret ?? [start + text.length, start + text.length];
    el.setSelectionRange(at[0], at[1]);
  };

  const onBodyKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Never edit mid-IME-composition: committing a CJK conversion fires Enter
    // with isComposing (Safari adds a stray keyCode-229 Enter after
    // compositionend) — intercepting it corrupts the composed text.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    const el = e.currentTarget;
    const { selectionStart: s, selectionEnd: en, value: v } = el;

    // Cmd/Ctrl+B: toggle **bold** around the selection.
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b") {
      e.preventDefault();
      const sel = v.slice(s, en);
      if (s === en) {
        applyEdit(s, en, "****", [s + 2, s + 2]);
      } else if (sel.includes("\n")) {
        // Bold renders per line — one **pair** spanning lines would show as
        // literal asterisk soup. Toggle each selected line individually,
        // keeping bullet markers outside the markers.
        const parts = sel.split("\n").map(line => {
          const p = line.match(BULLET_PREFIX_RE)?.[1] ?? "";
          return { p, t: line.slice(p.length).trim() };
        });
        const wrapped = (t: string) => t.startsWith("**") && t.endsWith("**") && t.length >= 4;
        const allBold = parts.every(({ t }) => !t || wrapped(t));
        const out = parts
          .map(({ p, t }) => (!t ? p : allBold ? p + t.slice(2, -2) : `${p}**${t}**`))
          .join("\n");
        applyEdit(s, en, out, [s, s + out.length]);
      } else if (sel.startsWith("**") && sel.endsWith("**") && sel.length >= 4) {
        const inner = sel.slice(2, -2);
        applyEdit(s, en, inner, [s, s + inner.length]);
      } else if (v.slice(Math.max(0, s - 2), s) === "**" && v.slice(en, en + 2) === "**") {
        applyEdit(s - 2, en + 2, sel, [s - 2, s - 2 + sel.length]);
      } else {
        applyEdit(s, en, `**${sel}**`, [s + 2, s + 2 + sel.length]);
      }
      return;
    }

    // Enter at the end of a bullet line continues the list; on a bare marker
    // it exits the list. Mid-line Enter falls through to a plain split.
    if (e.key === "Enter" && !e.shiftKey && s === en) {
      if (s !== v.length && v[s] !== "\n") return;
      const lineStart = v.lastIndexOf("\n", s - 1) + 1;
      const line = v.slice(lineStart, s);
      if (line && BARE_BULLET_RE.test(line)) {
        e.preventDefault();
        applyEdit(lineStart, s, "");
        return;
      }
      const m = line.match(BULLET_PREFIX_RE);
      if (m && line.length > m[1].length) {
        e.preventDefault();
        applyEdit(s, s, `\n${m[1]}`);
      }
    }
  };

  const insertTemplate = () => {
    const el = bodyRef.current;
    if (!el) return;
    applyEdit(0, el.value.length, WEEKLY_TEMPLATE, [TEMPLATE_CARET, TEMPLATE_CARET]);
  };

  // Memoized so composer keystrokes (draftBody state lives in this component)
  // don't re-parse and reconcile the whole update history on every character.
  const timelineItems = React.useMemo(
    () =>
      updates.map((u, idx) => {
        const author = u.authorId ? roster.find(p => p.id === u.authorId) : undefined;
        // The pill earns its ink only when this update CHANGED the status
        // (newest-first: compare with the next-older entry). The oldest
        // update establishes it, so it shows.
        const statusChanged = idx === updates.length - 1 || u.status !== updates[idx + 1].status;
        return (
          <li key={u.id}>
            <span className="ut-dot" />
            <div className="ut-row1">
              <span className="ut-when">{new Date(u.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
              {statusChanged && <StatusPill status={u.status} />}
              {author && <span className="ut-by">· {author.name}</span>}
            </div>
            <div className="ut-body">{renderUpdateBody(u.body)}</div>
          </li>
        );
      }),
    [updates, roster]
  );

  return (
    <>
      <div className={`im-backdrop ${open ? "open" : ""}`} onClick={onClose} />
      <div
        ref={drawerRef}
        className={`im ${open ? "open" : ""}`}
        role="dialog"
        aria-modal={open ? "true" : undefined}
        aria-hidden={!open}
        aria-labelledby={initiative ? "drawer-title" : undefined}
        tabIndex={-1}
      >
        {initiative && (
          <>
            <div className="im-header">
              <div className="im-head-main">
                <div className="im-eyebrow">
                  {team?.name}
                  {pod && <><span style={{ margin: "0 6px", opacity: 0.5 }}>›</span>{pod.name}</>}
                </div>
                {editing && edit ? (
                  <>
                    <input className="edit-name" value={edit.name} onChange={e => editField("name", e.target.value)} placeholder="Initiative name" />
                    <textarea
                      ref={tldrRef}
                      className="edit-tldr"
                      aria-label="Description"
                      aria-invalid={descriptionCleared || undefined}
                      value={edit.tldr}
                      onChange={e => editField("tldr", e.target.value)}
                      placeholder={DESCRIPTION_PLACEHOLDER}
                      maxLength={DESCRIPTION_MAX}
                      rows={2}
                    />
                    {edit.tldr.length >= DESCRIPTION_COUNTER_AT && (
                      <span className="edit-tldr-hint"><DescriptionCounter length={edit.tldr.length} /></span>
                    )}
                    {descriptionCleared && <span className="kv-error">A description is required.</span>}
                  </>
                ) : (
                  <>
                    <h2 id="drawer-title">{initiative.name}</h2>
                    {initiative.tldr.trim() ? (
                      <p className="tldr">{initiative.tldr}</p>
                    ) : (
                      // Historical rows predate the required field. A quiet
                      // affordance in the same slot invites a backfill without
                      // blocking anything.
                      <button type="button" className="tldr-add" data-skip-initial-focus onClick={() => startEdit("tldr")}>
                        Add a description so others know what this is
                      </button>
                    )}
                  </>
                )}

                <div className="im-pills">
                  {editing && edit ? (
                    <label className="edit-pill">
                      <span className="tiny-cap">Status</span>
                      <select value={edit.status} onChange={e => editField("status", e.target.value as Status)}>
                        {STATUS_KEYS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                      </select>
                    </label>
                  ) : (
                    <>
                      <StatusPill status={initiative.status} />
                      {dri && (
                        <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--muted-ink)" }}>
                          <Avatar person={dri} size="sm" />
                          <span>{dri.name}</span>
                          {!dri.email && <UnlinkedBadge />}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {/* The transition prompt: changing status to a committed state
                    asks for the real-world date RIGHT HERE, at the point of
                    action — prefilled, backdatable, and required to save. */}
                {(pendingStart || pendingShip || reopening) && edit && (
                  <div className="im-transition">
                    {pendingStart && (
                      <label className="im-transition-field">
                        <span className="tiny-cap">Started on</span>
                        <input
                          type="date"
                          value={edit.startISO}
                          max={localISO(new Date())}
                          onChange={e => editField("startISO", e.target.value)}
                        />
                      </label>
                    )}
                    {pendingShip && (
                      <label className="im-transition-field">
                        <span className="tiny-cap">Shipped on</span>
                        <input
                          type="date"
                          value={edit.shippedISO}
                          min={edit.startISO || undefined}
                          max={localISO(new Date())}
                          onChange={e => editField("shippedISO", e.target.value)}
                        />
                      </label>
                    )}
                    <span className="im-transition-note">
                      {reopening
                        ? "Reopening — the shipped date will be cleared."
                        : pendingStart && pendingShip
                          ? "Shipping directly — when did work start, and when did it ship?"
                          : pendingShip
                            ? "When did it actually ship? Backdate if needed."
                            : "When did work actually start? Backdate if needed."}
                    </span>
                    {datesInvalid && <span className="kv-error">Started must come before shipped.</span>}
                    {transitionMissing && <span className="kv-error">A date is required to change the status.</span>}
                  </div>
                )}
              </div>

              {/* Actions live in the header — the old bottom footer sat under
                  toasts and pushed the update feed above the fold. */}
              <div className="im-head-actions">
                {editing ? (
                  <>
                    <button className="btn ghost" onClick={cancelEdit}>Cancel</button>
                    <button className="btn primary" onClick={saveEdit} disabled={saveBlocked} title={datesInvalid ? "Start date is after end date" : targetOutside ? "Target is outside the defined fiscal years" : transitionMissing ? "Set the transition date first" : descriptionCleared ? "A description is required" : ownerMissing ? "Pick an owner first" : undefined}>
                      <Icon name="check" size={13} /> Save
                    </button>
                  </>
                ) : (
                  <>
                    <button className="btn" onClick={() => startEdit()}><Icon name="edit" size={13} /> Edit</button>
                    {/* One click, no confirm step — archive is a soft delete and the
                        toast carries Undo (see app-shell onArchive). */}
                    <button className="btn ghost" onClick={() => { onArchive(initiative.id); onClose(); }}><Icon name="archive" size={13} /> Archive</button>
                  </>
                )}
                <button className="im-close" onClick={onClose} aria-label="Close">
                  <Icon name="x" size={14} />
                </button>
              </div>
            </div>

            <div className="im-body">
              <div className="im-rail">
              <dl className="kv">
                {/* Team/pod/owner live in the header (eyebrow + DRI) — the rail
                    repeats them only while editing, where they're the editors. */}
                {editing && edit && (
                  <>
                    <dt>Team</dt>
                    <dd>
                      <select className="edit-inline" value={edit.teamId} onChange={e => editField("teamId", e.target.value)}>
                        {TEAMS.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </dd>

                    <dt>Pod</dt>
                    <dd>
                      {(() => {
                        const editTeam = TEAMS.find(t => t.id === edit.teamId);
                        const pods = editTeam?.pods ?? [];
                        return (
                          <select className="edit-inline" value={edit.podId} onChange={e => editField("podId", e.target.value)}>
                            <option value="">— None —</option>
                            {pods.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        );
                      })()}
                    </dd>

                    <dt>Owner</dt>
                    <dd>
                      <DriPicker
                        roster={roster}
                        value={edit.driId}
                        onChange={id => editField("driId", id)}
                        onPickPerson={onPickPerson}
                      />
                    </dd>
                  </>
                )}

                <dt>Target</dt>
                <dd>
                  {editing && edit ? (
                    <>
                      <input className="edit-inline" type="date" aria-label="Target date" value={edit.targetISO} min={edit.startISO || undefined} onChange={e => editField("targetISO", e.target.value)} />
                      {edit.targetISO && (() => {
                        const q = quarterForDate(new Date(edit.targetISO + "T00:00:00"));
                        return q
                          ? <span className="hint">Falls in {quarterLabel(q)}</span>
                          : <span className="kv-error">Outside the defined fiscal years.</span>;
                      })()}
                    </>
                  ) : (
                    <>{quarterLabel(initiative.targetQ)}{targetDate ? ` · ${fmtDateLong(targetDate)}` : ""}</>
                  )}
                </dd>

                {/* Estimate re-committed after work started — keep the original visible */}
                {slipped && !editing && originalTargetDate && (
                  <>
                    <dt>Originally</dt>
                    <dd className="kv-baseline">{fmtDateLong(originalTargetDate)}</dd>
                  </>
                )}

                {editing && edit ? (
                  <>
                    {/* During a status transition, the header callout owns the
                        transitioning date — never show the same field twice. */}
                    {!pendingStart && (
                      <>
                        <dt>{isCommittedStatus(edit.status) ? "Started" : "Planned start"}</dt>
                        <dd>
                          <input className="edit-inline" type="date" aria-label="Start date" value={edit.startISO} onChange={e => editField("startISO", e.target.value)} />
                        </dd>
                      </>
                    )}
                    {edit.status === "shipped" && !pendingShip && (
                      <>
                        <dt>Shipped</dt>
                        <dd>
                          <input className="edit-inline" type="date" aria-label="Shipped date" value={edit.shippedISO} min={edit.startISO || undefined} onChange={e => editField("shippedISO", e.target.value)} />
                          {datesInvalid && <span className="kv-error">Started is after shipped</span>}
                        </dd>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {startDate && <><dt>{isCommittedStatus(initiative.status) ? "Started" : "Planned start"}</dt><dd>{fmtDateLong(startDate)}</dd></>}
                    {shippedDate && <><dt>Shipped</dt><dd>{fmtDateLong(shippedDate)}</dd></>}
                    {/* The retro sentence: actual vs the estimate made when work began */}
                    {delta !== null && (
                      <>
                        <dt>Outcome</dt>
                        <dd className={delta > 0 ? "kv-delta-late" : "kv-delta-early"}>
                          {delta === 0
                            ? "On target"
                            : delta > 0
                              ? `${delta} day${delta === 1 ? "" : "s"} late`
                              : `${-delta} day${delta === -1 ? "" : "s"} early`}
                          {slipped && " vs. original estimate"}
                        </dd>
                      </>
                    )}
                  </>
                )}

                {editing && edit && (
                  <>
                    <dt>Jira</dt>
                    <dd><input className="edit-inline" type="text" value={edit.jira} onChange={e => editField("jira", e.target.value)} placeholder="(optional)" /></dd>
                    <dt>Brief</dt>
                    <dd><input className="edit-inline" type="text" value={edit.notion} onChange={e => editField("notion", e.target.value)} placeholder="(optional)" /></dd>
                    <dt>Design</dt>
                    <dd><input className="edit-inline" type="text" value={edit.design} onChange={e => editField("design", e.target.value)} placeholder="(optional — designs / prototype)" /></dd>
                    <dt>Depends on</dt>
                    <dd>
                      <select className="edit-inline" value={edit.dependsOnId} onChange={e => editField("dependsOnId", e.target.value)} aria-label="Depends on">
                        <option value="">(none)</option>
                        {dependencyGroupsByTeam.map(g => (
                          <optgroup key={g.id} label={g.name}>
                            {g.items.map(i => <option key={i.id} value={i.id}>{i.name} · {quarterLabel(i.targetQ)}</option>)}
                          </optgroup>
                        ))}
                      </select>
                    </dd>
                    <dt>Workstream</dt>
                    <dd>
                      <input
                        className="edit-inline"
                        type="text"
                        list="drawer-workstreams"
                        maxLength={80}
                        value={edit.workstream}
                        onChange={e => editField("workstream", e.target.value)}
                        placeholder="(optional — e.g. Test automation)"
                        aria-label="Workstream"
                      />
                      <datalist id="drawer-workstreams">
                        {workstreams.map(w => <option key={w} value={w} />)}
                      </datalist>
                      <span className="kv-hint">The effort this is part of, like Test automation. Can span teams and pods.</span>
                    </dd>
                  </>
                )}

                {!editing && initiative.workstream && (
                  <>
                    <dt>Workstream</dt>
                    <dd>{initiative.workstream}</dd>
                  </>
                )}

                {!editing && (initiative.dependsOnId || followers.length > 0) && (
                  <>
                    {initiative.dependsOnId && (
                      <>
                        <dt>Depends on</dt>
                        <dd>
                          {predecessor
                            ? <button type="button" className="dep-link" onClick={() => onOpen?.(predecessor)}>{predecessor.name}</button>
                            : <span className="dep-missing">An initiative that's no longer on the roadmap</span>}
                        </dd>
                      </>
                    )}
                    {followers.length > 0 && (
                      <>
                        <dt>Followed by</dt>
                        <dd className="dep-list">
                          {followers.map(f => (
                            <button key={f.id} type="button" className="dep-link" onClick={() => onOpen?.(f)}>{f.name}</button>
                          ))}
                        </dd>
                      </>
                    )}
                  </>
                )}
              </dl>

              <div>
                <h4 className="section-h">Tags <span className="rule" /></h4>
                {editing && edit ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span className="kv-hint">The goals this initiative serves.</span>
                    {tagPickerOptions(edit.teamId, edit.podId, edit.pillarIds).map(p => {
                      const on = edit.pillarIds.includes(p.id);
                      return (
                        <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, cursor: "pointer" }}>
                          <input type="checkbox" checked={on} onChange={() => togglePillar(p.id)} />
                          {tagNum(p) && <span className="pillar-chip-num">{tagNum(p)}</span>}
                          <span style={{ color: "var(--ink-2)" }}>{p.name}</span>
                          {p.retired && <span style={{ color: "var(--muted-2)", fontStyle: "italic", fontSize: 12 }}>retired</span>}
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  pillars.length === 0 ? (
                    <span style={{ color: "var(--muted-2)", fontStyle: "italic", fontSize: 13 }}>None tagged</span>
                  ) : (
                    <div className="pillar-chips" style={{ gap: 6 }}>
                      {pillars.map(p => (
                        <span key={p.id} className="pillar-chip" title={p.name}>
                          {tagNum(p) && <span className="pillar-chip-num">{tagNum(p)}</span>}
                          <span className="pillar-chip-label">{p.short}</span>
                        </span>
                      ))}
                    </div>
                  )
                )}
              </div>

              {(initiative.notion || initiative.jira || initiative.design) && (
                <div>
                  <h4 className="section-h">Linked sources <span className="rule" /></h4>
                  <div className="linkout-row">
                    {initiative.design && (
                      <a className="linkout" href={initiative.design.startsWith("http") ? initiative.design : `https://${initiative.design}`} title={initiative.design} target="_blank" rel="noopener noreferrer">
                        <span><span className="src">Design</span>Open</span>
                        <Icon name="external" size={14} />
                      </a>
                    )}
                    {initiative.notion && (
                      <a className="linkout" href={initiative.notion.startsWith("http") ? initiative.notion : `https://${initiative.notion}`} title={initiative.notion} target="_blank" rel="noopener noreferrer">
                        <span><span className="src">Brief</span>Open</span>
                        <Icon name="external" size={14} />
                      </a>
                    )}
                    {initiative.jira && (
                      <a className="linkout" href={jiraHref(initiative.jira)} title={initiative.jira} target="_blank" rel="noopener noreferrer">
                        <span><span className="src">Jira</span>{jiraDisplay(initiative.jira)}</span>
                        <Icon name="external" size={14} />
                      </a>
                    )}
                  </div>
                </div>
              )}
              </div>

              {/* The pulse: composer + history, independently scrollable.
                  Stays visible during edit — the conversation never
                  disappears out from under the reader. */}
              <div className="im-feed">
                <div>
                  <h4 className="section-h">Updates <span className="rule" /></h4>

                  <div className="update-post">
                    <div className="update-post-row">
                      <label className="up-field">
                        <span className="tiny-cap">Status</span>
                        <select value={draftStatus} onChange={e => setDraftStatus(e.target.value as Status)}>
                          {STATUS_KEYS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                        </select>
                      </label>
                      {/* Status transitions confirm their real-world date here —
                          backdatable, so nobody has to post on the exact day. */}
                      {startsWork && (
                        <label className="up-field">
                          <span className="tiny-cap">Started on</span>
                          <input
                            type="date"
                            value={transStartISO}
                            max={localISO(new Date())}
                            onChange={e => setTransStartISO(e.target.value)}
                          />
                        </label>
                      )}
                      {shipsNow && (
                        <label className="up-field">
                          <span className="tiny-cap">Shipped on</span>
                          <input
                            type="date"
                            value={transShippedISO}
                            max={localISO(new Date())}
                            onChange={e => setTransShippedISO(e.target.value)}
                          />
                        </label>
                      )}
                    </div>
                    {/* Authorship is never a choice — no picker, no "posting as"
                        display. SSO decides: updates post as the signed-in Entra
                        identity (shown on each posted update), and with auth on
                        an unresolved identity disables posting entirely. */}
                    {AUTH_ENABLED && !currentUser && (
                      <span className="hint" style={{ display: "block", marginBottom: 8 }}>
                        Sign in to post updates.
                      </span>
                    )}
                    {initiative.status === "shipped" && draftStatus !== "shipped" && (
                      <span className="hint" style={{ display: "block", marginBottom: 8 }}>
                        Reopening — the shipped date will be cleared.
                      </span>
                    )}
                    <textarea
                      ref={bodyRef}
                      className="edit-tldr"
                      value={draftBody}
                      onChange={e => setDraftBody(e.target.value)}
                      onKeyDown={onBodyKeyDown}
                      placeholder="What happened this week? Try: **Progress** … **Challenges** … **Next week** …"
                      rows={2}
                      maxLength={UPDATE_BODY_MAX}
                    />
                    {transInvalid && (
                      <span className="kv-error">Started must come before shipped.</span>
                    )}
                    <div className="update-post-foot">
                      {!draftBody.trim() && (
                        <button type="button" className="up-template" onClick={insertTemplate}>
                          Use weekly format
                        </button>
                      )}
                      {/* Disabled while the draft is only the untouched template
                          scaffold — and, with auth on, until the signed-in
                          identity resolves (never post as anyone else). */}
                      <button
                        className="btn primary"
                        onClick={submitUpdate}
                        disabled={!hasRealContent(draftBody) || transInvalid || (AUTH_ENABLED && !currentUser)}
                        title={AUTH_ENABLED && !currentUser ? "Sign in to post updates" : transInvalid ? "Started must come before shipped" : undefined}
                      >
                        <Icon name="check" size={13} /> Post update
                      </button>
                    </div>
                  </div>

                  {updates.length > 0 ? (
                    <ul className="update-timeline">{timelineItems}</ul>
                  ) : (
                    <p className="update-empty">{updatesHydrated ? "No updates yet — post the first one above." : "Loading updates…"}</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
